/**
 * Scoped-permission primitives.
 *
 * A scoped permission answers "for entity X and action Y, how much data may the
 * actor touch?". The answer is one of five ORDERED scopes. Resolution picks the
 * MAX scope the actor holds for a given `entity.action`, so the order below is
 * load-bearing (index = rank).
 *
 *   own            – only rows the actor owns
 *   same_position  – rows owned by anyone sharing one of the actor's positions
 *   same_oe        – rows owned by anyone in one of the actor's organization units
 *   child_oe       – same_oe PLUS every descendant OE (inclusive of the actor's OE)
 *   all            – everything in the actor's organization (never crosses orgs)
 */

export const PERMISSION_SCOPE = {
  OWN: 'own',
  SAME_POSITION: 'same_position',
  SAME_OE: 'same_oe',
  CHILD_OE: 'child_oe',
  ALL: 'all',
} as const;

export type PermissionScope = (typeof PERMISSION_SCOPE)[keyof typeof PERMISSION_SCOPE];

export const PERMISSION_ACTION = {
  CREATE: 'create',
  READ: 'read',
  UPDATE: 'update',
  DELETE: 'delete',
} as const;

export type PermissionAction = (typeof PERMISSION_ACTION)[keyof typeof PERMISSION_ACTION];

/**
 * Any catalog action key persisted in `role_permission.action` — the four CRUD
 * verbs OR a module-defined custom action (e.g. 'sendEmail'). The DB column is
 * varchar, so action keys are free-text like `entity`; use {@link PermissionAction}
 * only where the value is known to be one of the four CRUD verbs.
 */
export type PermissionActionKey = string;

/** ORDERED ascending. The index of a scope is its rank; higher rank = broader access. */
export const SCOPE_ORDER: readonly PermissionScope[] = [
  PERMISSION_SCOPE.OWN,
  PERMISSION_SCOPE.SAME_POSITION,
  PERMISSION_SCOPE.SAME_OE,
  PERMISSION_SCOPE.CHILD_OE,
  PERMISSION_SCOPE.ALL,
];

export const PERMISSION_ACTIONS: readonly PermissionAction[] = [
  PERMISSION_ACTION.CREATE,
  PERMISSION_ACTION.READ,
  PERMISSION_ACTION.UPDATE,
  PERMISSION_ACTION.DELETE,
];

/** Rank of a scope (0 = narrowest `own`). Unknown scopes rank -1. */
export function scopeRank(scope: PermissionScope): number {
  return SCOPE_ORDER.indexOf(scope);
}

/** Return the broader of two scopes. Used to fold a user's many roles into one. */
export function maxScope(a: PermissionScope, b: PermissionScope): PermissionScope {
  return scopeRank(a) >= scopeRank(b) ? a : b;
}

/** True when `have` is at least as broad as `need`. */
export function scopeAtLeast(have: PermissionScope, need: PermissionScope): boolean {
  return scopeRank(have) >= scopeRank(need);
}

/** Build the `${entity}.${action}` key used in the resolved scope map. */
export function scopeKey(entity: string, action: PermissionActionKey): string {
  return `${entity}.${action}`;
}

export function isPermissionScope(value: unknown): value is PermissionScope {
  return typeof value === 'string' && (SCOPE_ORDER as readonly string[]).includes(value);
}

export function isPermissionAction(value: unknown): value is PermissionAction {
  return typeof value === 'string' && (PERMISSION_ACTIONS as readonly string[]).includes(value);
}
